// Updated useBookingNotifications.js with URL routing support

import { useEffect, useRef } from 'react';
import { notification } from 'antd';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

export const useBookingNotifications = (bookings, options = {}) => {
    const {
        enableNotifications = true,
        enableSound = true,
        restaurantId, // Add restaurant ID for URL routing
    } = options;

    const previousBookingsRef = useRef([]);
    const audioRef = useRef(null);
    const isInitialLoadRef = useRef(true);
    const navigate = useNavigate();
    const isMobile = () => {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
            (typeof window.orientation !== "undefined") ||
            (navigator.userAgent.indexOf('IEMobile') !== -1);
    };
    // Initialize audio for notifications
    useEffect(() => {
        if (enableSound && !audioRef.current) {
            audioRef.current = new Audio();
            // Simple notification sound - you can replace this with your own sound file
            audioRef.current.src = '/sounds/notification.wav';
            audioRef.current.volume = 0.8;
            audioRef.current.preload = 'auto';
        }
    }, [enableSound]);

    // Request notification permission
    useEffect(() => {
        if (enableNotifications && 'Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission();
        }
    }, [enableNotifications]);

    // Function to handle notification click
    // Function to handle notification click
    const handleNotificationClick = (booking) => {
        if (restaurantId && booking.uniqueIdentificationToken) {
            // Focus window first
            if (window.focus) {
                window.focus();
            }

            // Call the global function directly
            if (window.openBookingFromNotification) {
                window.openBookingFromNotification(booking.uniqueIdentificationToken);
            } else {
                // Fallback to URL navigation
                const bookingUrl = `/admin/locations/${restaurantId}/bookings?booking=${booking.uniqueIdentificationToken}`;
                navigate(bookingUrl);
            }
        }
    };

    // Check for new bookings and send notifications
    useEffect(() => {
        // Skip if notifications disabled or no bookings
        if (!enableNotifications || !bookings || !Array.isArray(bookings) || bookings.length === 0) {
            return;
        }

        // MOBILE SAFEGUARD: Prevent any notification processing if page is hidden
        if (isMobile() && document.hidden) {
            return;
        }

        // Skip initial load
        if (isInitialLoadRef.current) {
            previousBookingsRef.current = bookings.map(booking => ({
                uniqueIdentificationToken: booking.uniqueIdentificationToken,
                status: booking.status,
                customerName: booking.customerName,
                partySize: booking.partySize,
                reservationDate: booking.reservationDate,
                hourStart: booking.hourStart
            }));
            isInitialLoadRef.current = false;
            return;
        }

        // Get previous bookings
        const previousBookings = previousBookingsRef.current;

        // Convert current bookings to comparison format
        const currentBookings = bookings.map(booking => ({
            uniqueIdentificationToken: booking.uniqueIdentificationToken,
            status: booking.status,
            customerName: booking.customerName,
            partySize: booking.partySize,
            reservationDate: booking.reservationDate,
            hourStart: booking.hourStart
        }));

        // Find new bookings
        const newBookings = currentBookings.filter(current =>
            !previousBookings.some(prev => prev.uniqueIdentificationToken === current.uniqueIdentificationToken)
        );

        // Find updated bookings
        const updatedBookings = currentBookings.filter(current => {
            const previous = previousBookings.find(prev => prev.uniqueIdentificationToken === current.uniqueIdentificationToken);
            return previous && (
                previous.status !== current.status ||
                previous.customerName !== current.customerName ||
                previous.partySize !== current.partySize ||
                previous.reservationDate !== current.reservationDate ||
                previous.hourStart !== current.hourStart
            );
        });

        // Handle new bookings
        if (newBookings.length > 0) {
            newBookings.forEach(booking => {
                const dateStr = booking.reservationDate ? booking.reservationDate.split('T')[0] : '';
                const hourStart = booking.hourStart ? booking.hourStart.split(':').slice(0, 2).join(':') : '';

                // Antd notification with click handler (always safe)
                const notificationConfig = {
                    message: 'Rezervare nouă!',
                    description: `${booking.customerName} - ${booking.partySize} persoane, ${moment(dateStr).format('DD.MM.YYYY')} la ${hourStart}`,
                    duration: 8,
                    placement: 'topRight',
                    onClick: () => handleNotificationClick(booking),
                    style: { cursor: 'pointer' }
                };

                notification.success(notificationConfig);

                // COMPLETELY SKIP BROWSER NOTIFICATIONS ON MOBILE
                if (!isMobile() && Notification.permission === 'granted' && !document.hidden) {
                    try {
                        const browserNotification = new Notification('Rezervare nouă!', {
                            body: `${booking.customerName} - ${booking.partySize} persoane, ${moment(dateStr).format('DD.MM.YYYY')} la ${hourStart}`,
                            icon: '/favicon.ico',
                            tag: `booking-${booking.uniqueIdentificationToken}`,
                            requireInteraction: true
                        });

                        // Handle browser notification click
                        browserNotification.onclick = (event) => {
                            event.preventDefault();
                            handleNotificationClick(booking);
                            browserNotification.close();
                        };

                        // Auto-close after 10 seconds
                        setTimeout(() => {
                            browserNotification.close();
                        }, 10000);
                    } catch (error) {
                        console.log('Browser notification failed:', error);
                    }
                }

                // Play sound (safe on mobile)
                if (enableSound && audioRef.current && !document.hidden) {
                    audioRef.current.play().catch(e => console.log('Could not play sound:', e));
                }
            });
        }

        // Handle updated bookings
        if (updatedBookings.length > 0) {
            updatedBookings.forEach(booking => {
                const previous = previousBookings.find(prev => prev.uniqueIdentificationToken === booking.uniqueIdentificationToken);

                let message = 'Rezervare actualizată!';
                let description = `${booking.customerName}`;

                if (previous && previous.status !== booking.status) {
                    const statusMap = {
                        'confirmed': 'confirmată',
                        'pending': 'în așteptare',
                        'cancelled': 'anulată'
                    };
                    description += ` - Status: ${statusMap[booking.status] || booking.status}`;
                }

                // Antd notification with click handler (always safe)
                const notificationConfig = {
                    message,
                    description,
                    duration: 5,
                    placement: 'topRight',
                    onClick: () => handleNotificationClick(booking),
                    style: { cursor: 'pointer' }
                };

                notification.info(notificationConfig);

                // COMPLETELY SKIP BROWSER NOTIFICATIONS ON MOBILE
                if (!isMobile() && Notification.permission === 'granted' && previous && previous.status !== booking.status && !document.hidden) {
                    try {
                        const browserNotification = new Notification('Rezervare actualizată!', {
                            body: description,
                            icon: '/favicon.ico',
                            tag: `booking-update-${booking.uniqueIdentificationToken}`
                        });

                        // Handle browser notification click
                        browserNotification.onclick = (event) => {
                            event.preventDefault();
                            handleNotificationClick(booking);
                            browserNotification.close();
                        };

                        // Auto-close after 8 seconds
                        setTimeout(() => {
                            browserNotification.close();
                        }, 8000);
                    } catch (error) {
                        console.log('Browser notification failed:', error);
                    }
                }
            });
        }

        // Update previous bookings reference
        previousBookingsRef.current = currentBookings;
    }, [bookings, enableNotifications, enableSound, restaurantId, navigate]);

    return {
        requestNotificationPermission: () => {
            if ('Notification' in window && Notification.permission === 'default') {
                return Notification.requestPermission();
            }
            return Promise.resolve(Notification.permission);
        },
        notificationPermission: typeof Notification !== 'undefined' ? Notification.permission : 'denied'
    };
};